import Big from 'big.js';
import axios from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as cc from 'smg/cc';
import * as countries from 'smg/countries';
import * as paymentoptions from 'smg/customer-api/paymentoptions';
import * as paypal from 'smg/customer-api/paypal';
import { Session } from 'smg/customer-api/session';
import * as users from 'smg/customer-api/users';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { Phone } from 'smg/phone';
import { MaybePromise } from 'smg/promise';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import * as cards from './cards';

declare interface myWindow extends Window {
    readonly staxOmniWebPaymentsToken: string;
}
declare const window: myWindow;

export const Get = paymentoptions.GetPaymentOptions;
export const New = paymentoptions.NewPaymentOption;

export class PaymentOptionFields {
    public readonly Phone: Phone;

    public readonly Countries: Promise<void>;

    constructor(
        public readonly AddressFields: HTMLFieldSetElement,
        public readonly SameAsShippingParent: HTMLElement,
        public readonly SameAsShipping: HTMLInputElement,
        public readonly FullName: HTMLInputElement,
        public readonly Company: HTMLInputElement,
        public readonly PhoneEl: HTMLInputElement,
        public readonly Email: HTMLInputElement,
        public readonly AddressLine1: HTMLInputElement,
        public readonly AddressLine2: HTMLInputElement,
        public readonly City: HTMLInputElement,
        public readonly State: HTMLSelectElement,
        public readonly Zip: HTMLInputElement,
        public readonly Country: HTMLSelectElement,
        public readonly PaymentOptionTypeParent: HTMLElement,
        public readonly PaymentOptionType: HTMLSelectElement,
        public readonly CcFields: HTMLFieldSetElement,
        public readonly BankFields: HTMLFieldSetElement,
        public readonly Card: HTMLInputElement,
        public readonly Month: HTMLSelectElement,
        public readonly Year: HTMLSelectElement,
        public readonly Security: HTMLInputElement,
        public readonly AccountNumber: HTMLInputElement,
        public readonly RoutingNumber: HTMLInputElement,
        public readonly BankChecking: HTMLInputElement,
        public readonly BankSavings: HTMLInputElement,
        public readonly BankPersonal: HTMLInputElement,
        public readonly BankBusiness: HTMLInputElement,
        paymentOptionTypeIDs?: number[],
    ) {
        const requiredBillingEls = [this.FullName, this.Country, this.AddressLine1, this.City, this.State, this.Zip, this.PhoneEl, this.Email];
        this.SameAsShipping.addEventListener('click', () => {
            if (this.SameAsShipping.checked) {
                this.AddressFields.classList.add('d-none');
                this.AddressFields.disabled = true;
                requiredBillingEls.forEach(el => el.setAttribute('disabled', ''));
            } else {
                this.AddressFields.classList.remove('d-none');
                this.AddressFields.disabled = false;
                requiredBillingEls.forEach(el => el.removeAttribute('disabled'));
            }
        });
        SameAsShipping.dispatchEvent(new Event('click'));

        this.Phone = new Phone(this.PhoneEl);

        this.PaymentOptionType.addEventListener('change', () => {
            const v = parseInt(this.PaymentOptionType.value, 10);

            this.CcFields.classList.add('d-none');
            this.CcFields.disabled = true;

            this.BankFields.classList.add('d-none');
            this.BankFields.disabled = true;

            switch (v) {
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT:
                this.CcFields.classList.remove('d-none');
                this.CcFields.disabled = false;
                break;
            case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK:
                this.BankFields.classList.remove('d-none');
                this.BankFields.disabled = false;
                break;
            }
        });

        if (paymentOptionTypeIDs?.length) {
            const allowed = new Set(paymentOptionTypeIDs);
            let remaining = 0;

            for (const option of Array.from(this.PaymentOptionType.options)) {
                if (!option.value) {
                    continue;
                }

                const v = parseInt(option.value, 10);
                if (!allowed.has(v)) {
                    option.parentElement?.removeChild(option);
                    continue;
                }

                remaining++;
            }

            if (remaining <= 1) {
                this.PaymentOptionTypeParent.classList.add('d-none');
                this.PaymentOptionType.options[this.PaymentOptionType.options.length - 1].selected = true;
            }
        }

        this.PaymentOptionType.dispatchEvent(new Event('change'));

        const y = new Date().getFullYear();
        for (let i = y; i < y + 50; i++) {
            this.Year.appendChild(html.FromString(`<option>${i}</option>`));
        }

        this.Countries = countries.Selects(this.Country, this.State, {
            defaultUnitedStates: true,
            stateParentSelector: 'div',
            phone: this.Phone,
            onStateHide: () => {
                this.State.disabled = true;
                this.Zip.parentElement?.classList.replace('col-md-2', 'col-md-6');
            },
            onStateShow: () => {
                this.State.disabled = false;
                this.Zip.parentElement?.classList.replace('col-md-6', 'col-md-2');
            },
        });
    }
}

// paypal - re add (orderIDs?: string[]) to params
export async function PromptNewPaymentOption(user?: users.UserDetails, confirmText: string = 'Next: Review Payment', paymentOptionTypeIDs?: number[], currentPaymentOptions?: models.PaymentOption): Promise<models.PaymentOption> {
    const body = template.Clone('#payment-option-modal-form-template');

    const fields = new PaymentOptionFields(
        body.querySelector('.address-fields') as HTMLFieldSetElement,
        body.querySelector('.same-as-shipping') as HTMLElement,
        body.querySelector('.same-as-shipping input') as HTMLInputElement,
        body.querySelector('#modal-full-name') as HTMLInputElement,
        body.querySelector('#modal-company') as HTMLInputElement,
        body.querySelector('#modal-phone') as HTMLInputElement,
        body.querySelector('#modal-email') as HTMLInputElement,
        body.querySelector('#modal-address-line-1') as HTMLInputElement,
        body.querySelector('#modal-address-line-2') as HTMLInputElement,
        body.querySelector('#modal-city') as HTMLInputElement,
        body.querySelector('#modal-state') as HTMLSelectElement,
        body.querySelector('#modal-zip') as HTMLInputElement,
        body.querySelector('#modal-country') as HTMLSelectElement,
        body.querySelector('#modal-payment-option-type-parent') as HTMLElement,
        body.querySelector('#modal-payment-option-type') as HTMLSelectElement,
        body.querySelector('.fields-cc') as HTMLFieldSetElement,
        body.querySelector('.fields-bank') as HTMLFieldSetElement,
        body.querySelector('#modal-credit-card-number') as HTMLInputElement,
        body.querySelector('#modal-credit-card-month') as HTMLSelectElement,
        body.querySelector('#modal-credit-card-year') as HTMLSelectElement,
        body.querySelector('#modal-credit-card-security') as HTMLInputElement,
        body.querySelector('#modal-account-number') as HTMLInputElement,
        body.querySelector('#modal-routing-number') as HTMLInputElement,
        body.querySelector('#modal-account-type-checking') as HTMLInputElement,
        body.querySelector('#modal-account-type-savings') as HTMLInputElement,
        body.querySelector('#modal-bank-type-personal') as HTMLInputElement,
        body.querySelector('#modal-bank-type-business') as HTMLInputElement,
        paymentOptionTypeIDs,
    );

    await fields.Countries;

    fields.SameAsShipping.checked = false;
    fields.SameAsShipping.dispatchEvent(new Event('click'));
    fields.SameAsShippingParent.parentElement?.removeChild(fields.SameAsShippingParent);

    fields.AddressFields.classList.remove('d-none');

    if (currentPaymentOptions) {
        fields.FullName.value = currentPaymentOptions.fullName || '';
        fields.Company.value = currentPaymentOptions.company || '';
        fields.Email.value = currentPaymentOptions.email || '';
        fields.PhoneEl.value = currentPaymentOptions.phone || '';
        fields.AddressLine1.value = currentPaymentOptions.addressLine1 || '';
        fields.AddressLine2.value = currentPaymentOptions.addressLine2 || '';
        fields.City.value = currentPaymentOptions.city || '';
        fields.Zip.value = currentPaymentOptions.zip || '';
        fields.Country.value = String(currentPaymentOptions.countryID || '');
        fields.Country.dispatchEvent(new Event('change'));
        fields.State.value = String(currentPaymentOptions.stateID || '');
        fields.PaymentOptionType.value = String(currentPaymentOptions.paymentOptionTypeID);
        fields.PaymentOptionType.dispatchEvent(new Event('change'));
        if (currentPaymentOptions.accountType === models.PaymentOptionAccountType.PAYMENT_OPTION_ACCOUNT_TYPE_CHECKING) {
            fields.BankChecking.checked = true;
        } else if (currentPaymentOptions.accountType === models.PaymentOptionAccountType.PAYMENT_OPTION_ACCOUNT_TYPE_SAVINGS) {
            fields.BankSavings.checked = true;
        }
        if (currentPaymentOptions.bankType === models.PaymentOptionBankType.PAYMENT_OPTION_BANK_TYPE_BUSINESS) {
            fields.BankBusiness.checked = true;
        } else if (currentPaymentOptions.bankType === models.PaymentOptionBankType.PAYMENT_OPTION_BANK_TYPE_PERSONAL) {
            fields.BankPersonal.checked = true;
        }
    }

    return new Promise(resolve => {
        new Modal({
            title: currentPaymentOptions ? 'Update Payment Method' : 'New Payment Method',
            outsideClosable: false,
            size: 'modal-lg',
            confirmText,
            body,
            onSubmit: async () => paymentoptions.NewPaymentOption({
                user: user || {
                    company: fields.Company.value,
                    email: fields.Email.value,
                    phone: await fields.Phone.Format(),
                    countryID: parseInt(fields.Country.value, 10),
                    fullName: fields.FullName.value,
                    stateID: parseInt(fields.State.value, 10),
                },
                paymentOption: {
                    fullName: fields.FullName.value,
                    company: fields.Company.value,
                    addressLine1: fields.AddressLine1.value,
                    addressLine2: fields.AddressLine2.value,
                    city: fields.City.value,
                    state: fields.State.value ? {
                        id: parseInt(fields.State.value, 10) as models.StateID,
                        stateCode: fields.State.getAttribute('data-state-code') || undefined,
                        name: fields.State.getAttribute('data-name') || undefined,
                    } : undefined,
                    zip: fields.Zip.value,
                    country: {
                        id: parseInt(fields.Country.value, 10) as models.CountryID,
                        countryCode2: fields.Country.getAttribute('data-country-code-2') || undefined,
                        countryCode3: fields.Country.getAttribute('data-country-code-3') || undefined,
                        name: fields.Country.getAttribute('data-name') || undefined,
                    },
                    phone: await fields.Phone.Format(),
                    email: fields.Email.value,
                    paymentOptionTypeID: (parseInt(fields.PaymentOptionType?.value || '1', 10) || 1) as models.PaymentOptionTypeID,
                },
                bankAccount: fields.AccountNumber.value,
                bankRouting: fields.RoutingNumber.value,
                accountType: fields.BankChecking.checked ? models.PaymentOptionAccountType.PAYMENT_OPTION_ACCOUNT_TYPE_CHECKING : models.PaymentOptionAccountType.PAYMENT_OPTION_ACCOUNT_TYPE_SAVINGS,
                bankType: fields.BankBusiness.checked ? models.PaymentOptionBankType.PAYMENT_OPTION_BANK_TYPE_BUSINESS : models.PaymentOptionBankType.PAYMENT_OPTION_BANK_TYPE_PERSONAL,
                creditCardNumber: fields.Card.value,
                expireMonth: parseInt(fields.Month.value, 10),
                expireYear: parseInt(fields.Year.value, 10),
                securityCode: fields.Security.value,
                fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
            }).then(async newPaymentOption => {
                if (currentPaymentOptions?.id) {
                    await paymentoptions.DeletePaymentOption(currentPaymentOptions.id);
                }
                toast.Success('Your payment method has been added to your account.');
                resolve(newPaymentOption);
            }),
        });
    });
}

export async function DrawPaymentOption(paymentOption: models.PaymentOption, orderPayment?: models.OrderPayment, el: HTMLElement = template.Clone('#payment-option-template')): Promise<HTMLElement> {
    const later = el.querySelector('.payment-option-later') as HTMLElement;
    const credit = el.querySelector('.payment-option-credit') as HTMLElement;
    const bank = el.querySelector('.payment-option-bank') as HTMLElement;
    const check = el.querySelector('.payment-option-check') as HTMLElement;
    const cash = el.querySelector('.payment-option-cash') as HTMLElement;
    const po = el.querySelector('.payment-option-po') as HTMLElement;
    // const paypal = el.querySelector('.payment-option-paypal') as HTMLElement;
    const balance = el.querySelector('.payment-option-balance') as HTMLElement;

    later.classList.add('d-none');
    credit.classList.add('d-none');
    bank.classList.add('d-none');
    check.classList.add('d-none');
    cash.classList.add('d-none');
    po.classList.add('d-none');
    // paypal.classList.add('d-none');
    balance.classList.add('d-none');

    switch (paymentOption.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_NONE:
        later.classList.remove('d-none');
        break;
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT: {
        credit.classList.remove('d-none');
        if (paymentOption.creditCardLast4) {
            credit.querySelector('.cc-last-4-parent')?.classList.remove('d-none');
            (credit.querySelector('.cc-last-4') as HTMLElement).textContent = String(paymentOption.creditCardLast4).padStart(4, '0');
        }
        (credit.querySelector('.cc-name') as HTMLElement).textContent = cc.Issuer(String(paymentOption.creditCardFirst6 || '')) || 'Card';
        const $issuerIcon = cc.IssuerIcon(String(paymentOption.creditCardFirst6 || ''));
        credit.querySelector('i.fab')?.setAttribute('class', `${Array.from($issuerIcon.classList).join(' ')} fa-lg`);
        if (paymentOption.creditCardExpirationDate) {
            credit.querySelector('.expiry')?.classList.remove('d-none');
            (credit.querySelector('.cc-expires') as HTMLElement).textContent = String(paymentOption.creditCardExpirationDate);
        }
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK: {
        const bankTypeContainer = el.querySelector<HTMLElement>('.payment-option-bank');

        if (bankTypeContainer) {
            bankTypeContainer.classList.remove('d-none');
            const accountType = bankTypeContainer?.querySelector<HTMLElement>('#account-type');
            const bankType = bankTypeContainer?.querySelector<HTMLElement>('#bank-type');

            if (accountType) {
                accountType.textContent = paymentOption.accountType || '';
            }

            if (bankType) {
                bankType.textContent = paymentOption.bankType || '';
            }
        }

        const bank = el.querySelector('.payment-option-bank') as HTMLElement;
        bank.classList.remove('d-none');
        if (paymentOption.creditCardLast4) {
            bank.querySelector('.ending-in')?.classList.remove('d-none');
            (bank.querySelector('.cc-last-4') as HTMLElement).textContent = String(paymentOption.creditCardLast4).padStart(4, '0');
        } else {
            bank.querySelector('.ending-in')?.classList.add('d-none');
        }
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK: {
        const check = el.querySelector('.payment-option-check') as HTMLElement;
        check.classList.remove('d-none');
        if (orderPayment?.checkNumber) {
            (check.querySelector('.check-number') as HTMLElement).textContent = `#${orderPayment.checkNumber}`;
        }
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CASH: {
        const cash = el.querySelector('.payment-option-cash') as HTMLElement;
        cash.classList.remove('d-none');
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE: {
        const wire = el.querySelector('.payment-option-wire') as HTMLElement;
        wire.classList.remove('d-none');
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
        const po = el.querySelector('.payment-option-po') as HTMLElement;
        po.classList.remove('d-none');
        if (orderPayment?.purchaseOrderNumber) {
            (po.querySelector('.po-number') as HTMLElement).textContent = `#${orderPayment.purchaseOrderNumber}`;
        }
        if (orderPayment?.checkNumber) {
            po.querySelector('h5')?.classList.remove('d-none');
            (po.querySelector('.check-number') as HTMLElement).textContent = `#${orderPayment.checkNumber}`;
        }
        break;
    }
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE: {
        const balance = el.querySelector('.payment-option-balance') as HTMLElement;
        balance.classList.remove('d-none');

        const availableBalance = el.querySelector('.available-balance') as HTMLElement;
        availableBalance.textContent = DollarFormat((await Session()).balance);
        break;
    }
    default:
        throw new Error(`Hey! We don't handle the payment method type "${paymentOption.paymentOptionTypeID || ''}"`);
    }

    return el;
}

export function DrawOrderPaymentFields(paymentOption: models.PaymentOption): HTMLElement | void {
    switch (paymentOption.paymentOptionTypeID) {
    case models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO: {
        const el = template.Clone('#new-order-payment-purchase-order');

        const poNumber = el.querySelector('#po-number') as HTMLLabelElement;
        const poNumberLabel = el.querySelector('[for="po-number"]') as HTMLLabelElement;

        const uploads = el.querySelector('#uploads') as HTMLInputElement;
        const uploadsLabel = el.querySelector('[for="uploads"]') as HTMLLabelElement;

        const thumbnails = el.querySelector('#uploads-thumbnails') as HTMLElement;

        const uuid = UUID();

        [poNumber, uploads, thumbnails].forEach(el => {
            el.classList.add(el.id);
            el.id = `${el.id}-${uuid}`;
        });
        [poNumberLabel, uploadsLabel].forEach(el => el.setAttribute('for', `${el.getAttribute('for') || ''}-${uuid}`));

        return el;
    }
    }
}

export async function PromptPaymentOption(user?: users.UserDetails, newConfirmText?: string, paymentOptionTypeIDs?: number[], disablePaypal: boolean = false, orderIDs?: string[]): Promise<models.PaymentOption> {
    const paymentOptions = await paymentoptions.GetPaymentOptions({
        'data': 'fullName,company,addressLine1,addressLine2,city,stateID,zip,countryID,phone,email,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID',
        'q': 'valid:true has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip',
        'sort': 'created desc',
    }).catch(() => []);
    if (!paymentOptions.length) {
        // paypal - add orderIDs as param
        return PromptNewPaymentOption(user, newConfirmText, paymentOptionTypeIDs, undefined);
    }

    let resolve: (value: MaybePromise<models.PaymentOption>) => void;
    const p = new Promise<models.PaymentOption>(r => resolve = r);

    const body = document.createDocumentFragment();

    if (!disablePaypal && orderIDs?.length) {
        const paypalAppend = body.appendChild(html.FromString(`
            <div>
                <button type="button" id="paypal-btn" class="btn mx-2 my-2 p-2" style="background-color: #FFD140;">Checkout with <i class="fab fa-paypal" style="color: #003087"></i><span class="fw-bold" style="color: #003087">Pay</span><span class="fw-bold" style="color: #0070E0">Pal</span></button>
                <div class="fw-bold m-2 mb-5">Or</div>
            </div>`));

        const paypalBtn = paypalAppend.querySelector('#paypal-btn') as HTMLButtonElement;
        paypalBtn.addEventListener('click', () => {
            buttons.Start(paypalBtn);
            paypal.CreateOrder({
                userDetails: user,
                orderIDs: orderIDs,
                successURL: `${window.location.href}${!window.location.href.includes('?') ? '?' : '&'}paypal=success`,
                cancelURL: window.location.href,
            }).then(resp => {
                window.location.href = resp;
            }).catch(err => {
                console.error(err);
                buttons.Stop(paypalBtn);
            });
        });
    }

    for (const paymentOption of paymentOptions) {
        const h = cards.DrawUse(await DrawPaymentOption(paymentOption), 'Use this payment method');
        h.querySelector('.use')?.addEventListener('click', e => {
            e.preventDefault();

            resolve(paymentOption);
            m.Close();
        });
        body.appendChild(h);
    }

    const m = new Modal({
        title: 'Select Payment Method',
        confirmText: 'New Payment Method',
        body,
        onSubmit: () => resolve(PromptNewPaymentOption(user, newConfirmText, paymentOptionTypeIDs)),
    });

    return p;
}

export async function PromptPayment(user: users.UserDetails | undefined, orderIDs: string[], defaultAmount: Big, disablePaypal: boolean = false): Promise<models.OrderPayment[]> {
    const paymentOption = await PromptPaymentOption(user, undefined, [models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT, models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BANK], disablePaypal, orderIDs);

    const body = template.Clone('#order-payment-modal-form-template');
    body.querySelector('#modal-payment-option')?.replaceWith(await DrawPaymentOption(paymentOption));
    (body.querySelector('#modal-balance-amount') as HTMLElement).textContent = DollarFormat(defaultAmount);
    const defaultRadio = body.querySelector('#modal-payment-amount-given') as HTMLInputElement;
    const customRadio = body.querySelector('#modal-payment-amount-custom') as HTMLInputElement;
    const customAmountParent = body.querySelector('#modal-custom-amount-parent') as HTMLElement;
    const customAmount = body.querySelector('#modal-custom-amount') as HTMLInputElement;
    customAmount.placeholder = defaultAmount.toFixed(2);
    customAmount.max = defaultAmount.toString();

    customRadio.addEventListener('click', () => {
        customAmountParent.classList.remove('d-none');
        customAmount.required = true;
        setTimeout(() => customAmount.focus());
    });
    defaultRadio.addEventListener('click', () => {
        customAmountParent.classList.add('d-none');
        customAmount.required = false;
    });

    return new Promise(resolve => {
        new Modal({
            title: 'Confirm Payment',
            outsideClosable: false,
            confirmText: 'Confirm Payment',
            body,
            onSubmit: () => axios.post<models.OrderPayment[]>('/order-payments', {
                'user': user,
                'orderIDs': orderIDs,
                'paymentOptionID': paymentOption.key,
                'amount': customRadio.checked ? customAmount.value : defaultAmount.round(2)?.toString(),
            }).then(resp => {
                toast.Success('Your payment has been received.');
                resolve(resp.data);
            }),
        });
    });
}

export async function GetBalancePaymentOption(): Promise<models.PaymentOption> {
    const paymentOptions = await paymentoptions.GetPaymentOptions({
        'data': 'paymentOptionTypeID',
        'q': `Type:${models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE}`,
        'sort': 'created desc',
        'max': 1,
    });

    if (paymentOptions.length) {
        return paymentOptions[0];
    }

    const sess = await Session();

    const user: users.UserDetails = {
        firstName: sess.firstName,
        lastName: sess.lastName,
        company: sess.company,
        email: sess.email,
        phone: sess.phone,
    };

    return await axios.post<models.PaymentOption>('/payment-options', {
        'user': user,
        'paymentOptionTypeID': models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE,
    }).then(resp => resp.data);
}
